import { swiperSlider } from '@theme/coco-utilities';

if (!customElements.get('announcement-bar-slider')) {
  class AnnouncementBarSlider extends HTMLDivElement {
    constructor() {
      super();
      this.section = this.closest('.shopify-section');
      this.slider = this.section?.querySelector('[data-slider]') ?? this.querySelector('[data-slider]');
      this.params = this.slider?.dataset.slider
        ? JSON.parse(this.slider.dataset.slider)
        : {};
    }

    connectedCallback() {
      swiperSlider(this.slider, this.params);
    }
  }

  customElements.define('announcement-bar-slider', AnnouncementBarSlider, { extends: 'div' });
}

if (!customElements.get('announcement-bar')) {
  class AnnouncementBar extends HTMLDivElement {
    constructor() {
      super();
      this.announcementBarHeight = 0;
      this._measureRaf = 0;
      this._resizeTimer = 0;
      this._pendingHeight = null;
      this._ro = null;
      this.scheduleMeasure = this.scheduleMeasure.bind(this);
      this.onResizeHandler = this.onResizeHandler.bind(this);
    }

    connectedCallback() {
      if (typeof ResizeObserver !== 'undefined') {
        // Prefer RO box size — no offsetHeight forced layout on resize
        this._ro = new ResizeObserver((entries) => {
          const entry = entries[0];
          if (!entry) return;
          const fromBox = entry.borderBoxSize?.[0]?.blockSize;
          const height =
            typeof fromBox === 'number' && fromBox > 0
              ? fromBox
              : entry.contentRect?.height || 0;
          if (height > 0) {
            this._pendingHeight = height;
            this.scheduleMeasure();
          }
        });
        this._ro.observe(this);
      } else {
        window.addEventListener('resize', this.onResizeHandler, { passive: true });
        this.scheduleMeasure();
      }

      // Fonts/layout settle — coalesced; uses pending RO height when available
      const onReady = () => this.scheduleMeasure();
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', onReady, { once: true });
      } else {
        requestAnimationFrame(onReady);
      }
    }

    disconnectedCallback() {
      this._ro?.disconnect();
      this._ro = null;
      window.removeEventListener('resize', this.onResizeHandler);
      if (this._measureRaf) {
        cancelAnimationFrame(this._measureRaf);
        this._measureRaf = 0;
      }
      clearTimeout(this._resizeTimer);
    }

    /**
     * Coalesce measure requests into a single rAF.
     * Uses ResizeObserver pending height when set — avoids offsetHeight.
     */
    scheduleMeasure() {
      if (this._measureRaf) return;
      this._measureRaf = requestAnimationFrame(() => {
        this._measureRaf = 0;
        this.commitHeight();
      });
    }

    commitHeight() {
      let height = this._pendingHeight;
      this._pendingHeight = null;

      // Fallback only when RO hasn't delivered a size yet
      if (!(height > 0)) {
        height = this.offsetHeight;
      }

      if (this.announcementBarHeight === height) {
        return;
      }

      this.announcementBarHeight = height;

      // Natural height only — header owns --dynamicAnnouncementHeight on scroll
      document.documentElement.style.setProperty('--announcementHeight', `${height.toFixed(1)}px`);

      document.dispatchEvent(
        new CustomEvent('announcement:height', {
          bubbles: true,
          detail: { height },
        }),
      );
    }

    onResizeHandler() {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => this.scheduleMeasure(), 100);
    }
  }

  customElements.define('announcement-bar', AnnouncementBar, { extends: 'div' });
}
